/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import './styles.css';
import { Row, Col, Container } from 'react-bootstrap';
import api from '../../../services/API';
import Select from 'react-select';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const ESTADOS_BRASIL = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

const MESES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

export default function SellOut() {
  const queryClient = useQueryClient();
  const [filtros, setFiltros] = useState({
    cidade: '',
    uf: '',
    vendedor: '',
    dental: '',
    produto: '',
    dataInicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    dataFim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
  });
  const ultimosFiltrosRef = useRef({ ...filtros });

  const [cardFlipped, setCardFlipped] = useState({
    valor: false,
    pontos: false,
    quantidade: false,
  });

  const [resumoAnoAnterior, setResumoAnoAnterior] = useState({
    valorProdutosVendidos: 0,
    quantidadePontosEntregues: 0,
    quantidadeProdutosVendidos: 0,
  });

  const [cidades, setCidades] = useState([]);
  const [dentais, setDentais] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [vendedores, setVendedores] = useState([]);

  const [resumo, setResumo] = useState({
    valorProdutosVendidos: 0,
    quantidadePontosEntregues: 0,
    quantidadeProdutosVendidos: 0,
  });

  const [vendasMensais, setVendasMensais] = useState([]);
  const [rankingVendedores, setRankingVendedores] = useState([]);
  const [rankingDentais, setRankingDentais] = useState([]);
  const [isDentalOnly, setIsDentalOnly] = useState(false);
  const [isRanking, setIsRanking] = useState(false);
  const [ordenarPor, setOrdenarPor] = useState('pontos');
  const [ordem, setOrdem] = useState('desc');
  const [buscandoResumo, setBuscandoResumo] = useState(false);
  const [buscandoAnoAnterior, setBuscandoAnoAnterior] = useState({
    valor: false,
    pontos: false,
    quantidade: false,
  });
  const [buscandoVendasMensais, setBuscandoVendasMensais] = useState(false);
  const [buscandoRanking, setBuscandoRanking] = useState(false);
  const [pagination, setPagination] = useState({
    nextCursor: null,
    prevCursor: null,
    hasNext: false,
    hasPrev: false,
    limit: 10,
    startPosition: 1,
    endPosition: 0,
  });
  const buscandoRankingRef = useRef(false);
  const buscandoResumoRef = useRef(false);
  const primeiraRenderizacaoRef = useRef(true);

  useEffect(() => {
    buscarCidades();
    buscarRanking();
    buscarDados(false, true);
    ultimosFiltrosRef.current = { ...filtros };
    primeiraRenderizacaoRef.current = false;
  }, []);

  useEffect(() => {
    if (primeiraRenderizacaoRef.current || buscandoRankingRef.current) {
      return;
    }
    buscarRankingComModo(isDentalOnly, isRanking);
  }, [isDentalOnly, isRanking]);

  useEffect(() => {
    if (primeiraRenderizacaoRef.current || buscandoRankingRef.current) {
      return;
    }
    buscarRankingComModo(isDentalOnly, isRanking);
  }, [ordenarPor, ordem]);

  useEffect(() => {
    buscarCidades();
  }, [filtros.uf]);

  useEffect(() => {
    buscarDentais();
  }, [filtros.cidade, filtros.uf, filtros.produto]);

  useEffect(() => {
    buscarVendedores();
  }, [filtros.dental, filtros.cidade, filtros.uf]);

  useEffect(() => {
    buscarProdutos();
  }, [filtros.dental]);

  const buscarCidades = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.uf) params.append('uf', filtros.uf);

      const filtrosKey = JSON.stringify({ uf: filtros.uf });
      const queryKey = ['filtros', 'cidades', filtrosKey];
      const cachedData = queryClient.getQueryData(queryKey);
      
      if (cachedData) {
        setCidades(cachedData);
      } else {
        const resultado = await api.get(`/api/sellout/filtros/cidades?${params.toString()}`);
        if (resultado && resultado.data.cidades) {
          setCidades(resultado.data.cidades);
          queryClient.setQueryData(queryKey, resultado.data.cidades);
        }
      }
    } catch (error) {
    }
  };

  const buscarDentais = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.produto) params.append('produto', filtros.produto);
      if (filtros.cidade) params.append('cidade', filtros.cidade);
      if (filtros.uf) params.append('uf', filtros.uf);

      const filtrosKey = JSON.stringify({ produto: filtros.produto, cidade: filtros.cidade, uf: filtros.uf });
      const queryKey = ['filtros', 'dentais', filtrosKey];
      const cachedData = queryClient.getQueryData(queryKey);
      
      if (cachedData) {
        setDentais(cachedData);
      } else {
        const resultado = await api.get(
          `/api/sellout/filtros/dentais?${params.toString()}`
        );
        if (resultado && resultado.data.dentais) {
          setDentais(resultado.data.dentais);
          queryClient.setQueryData(queryKey, resultado.data.dentais);
        }
      }
    } catch (error) {
    }
  };

  const buscarProdutos = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.dental) params.append('revenda', filtros.dental);

      const filtrosKey = JSON.stringify({ revenda: filtros.dental });
      const queryKey = ['filtros', 'produtos', filtrosKey];
      const cachedData = queryClient.getQueryData(queryKey);
      
      if (cachedData) {
        setProdutos(cachedData);
        return cachedData;
      } else {
        const resultado = await api.get(
          `/api/sellout/filtros/produtos?${params.toString()}`
        );
        if (resultado && resultado.data.produtos) {
          const produtosFormatados = resultado.data.produtos.map((produto) => ({
            value: produto.id,
            label: produto.nome,
          }));
          setProdutos(produtosFormatados);
          queryClient.setQueryData(queryKey, produtosFormatados);
          return produtosFormatados;
        }
        return [];
      }
    } catch (error) {
      return [];
    }
  };


  const buscarVendedores = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.dental) params.append('revenda', filtros.dental);
      if (filtros.cidade) params.append('cidade', filtros.cidade);
      if (filtros.uf) params.append('uf', filtros.uf);

      const filtrosKey = JSON.stringify({ revenda: filtros.dental, cidade: filtros.cidade, uf: filtros.uf });
      const queryKey = ['filtros', 'vendedores', filtrosKey];
      const cachedData = queryClient.getQueryData(queryKey);
      
      if (cachedData) {
        setVendedores(cachedData);
      } else {
        const resultado = await api.get(
          `/api/sellout/filtros/vendedores?${params.toString()}`
        );
        if (resultado && resultado.data.vendedores) {
          setVendedores(resultado.data.vendedores);
          queryClient.setQueryData(queryKey, resultado.data.vendedores);
        }
      }
    } catch (error) {
    }
  };

  const buscarResumoApenas = async (anoAnterior = false) => {
    if (buscandoResumoRef.current && !anoAnterior) {
      return;
    }
    
    if (!anoAnterior) {
      buscandoResumoRef.current = true;
      setBuscandoResumo(true);
    }
    
    try {
      const params = new URLSearchParams();
      if (filtros.cidade) params.append('cidade', filtros.cidade);
      if (filtros.uf) params.append('uf', filtros.uf);
      if (filtros.vendedor) params.append('vendedor', filtros.vendedor);
      if (filtros.dental) params.append('revenda', filtros.dental);
      if (filtros.produto) params.append('produto', filtros.produto);
      
      if (filtros.dataInicio && filtros.dataFim) {
        if (anoAnterior) {
          const dataInicio = new Date(filtros.dataInicio);
          const dataFim = new Date(filtros.dataFim);
          dataInicio.setFullYear(dataInicio.getFullYear() - 1);
          dataFim.setFullYear(dataFim.getFullYear() - 1);
          params.append('dataInicio', dataInicio.toISOString().split('T')[0]);
          params.append('dataFim', dataFim.toISOString().split('T')[0]);
        } else {
          params.append('dataInicio', filtros.dataInicio);
          params.append('dataFim', filtros.dataFim);
        }
      }

      const resumoFiltrosKey = JSON.stringify(filtros);
      const resumoQueryKey = anoAnterior 
        ? ['resumo', 'anoAnterior', resumoFiltrosKey]
        : ['resumo', resumoFiltrosKey];
      
      const cachedResumo = queryClient.getQueryData(resumoQueryKey);
      
      let resumoResult;
      if (cachedResumo) {
        resumoResult = { data: cachedResumo };
      } else {
        resumoResult = await api.get(`/api/sellout/resumo?${params.toString()}`);
        queryClient.setQueryData(resumoQueryKey, resumoResult.data);
      }
      
      if (resumoResult && resumoResult.data) {
        if (anoAnterior) {
          setResumoAnoAnterior(resumoResult.data);
        } else {
          setResumo(resumoResult.data);
        }
      }
    } catch (error) {
    } finally {
      setBuscandoResumo(false);
      if (!anoAnterior) {
        buscandoResumoRef.current = false;
      }
    }
  };

  const buscarDados = async (anoAnterior = false, buscarGrafico = true, filtrosParaUsar = null) => {
    if (buscandoResumoRef.current && !anoAnterior) {
      return;
    }

    if (!anoAnterior) {
      buscandoResumoRef.current = true;
      setBuscandoResumo(true);
      if (buscarGrafico) {
        setBuscandoVendasMensais(true);
      }
    }

    try {
      const filtrosAtuais = filtrosParaUsar || filtros;
      const params = new URLSearchParams();
      if (filtrosAtuais.cidade) params.append('cidade', filtrosAtuais.cidade);
      if (filtrosAtuais.uf) params.append('uf', filtrosAtuais.uf);
      if (filtrosAtuais.vendedor) params.append('vendedor', filtrosAtuais.vendedor);
      if (filtrosAtuais.dental) params.append('revenda', filtrosAtuais.dental);
      if (filtrosAtuais.produto) params.append('produto', filtrosAtuais.produto);
      
      if (filtrosAtuais.dataInicio && filtrosAtuais.dataFim) {
        if (anoAnterior) {
          const dataInicio = new Date(filtrosAtuais.dataInicio);
          const dataFim = new Date(filtrosAtuais.dataFim);
          dataInicio.setFullYear(dataInicio.getFullYear() - 1);
          dataFim.setFullYear(dataFim.getFullYear() - 1);
          params.append('dataInicio', dataInicio.toISOString().split('T')[0]);
          params.append('dataFim', dataFim.toISOString().split('T')[0]);
        } else {
          params.append('dataInicio', filtrosAtuais.dataInicio);
          params.append('dataFim', filtrosAtuais.dataFim);
        }
      }

      const resumoFiltrosKey = JSON.stringify(filtrosAtuais);
      const resumoQueryKey = anoAnterior 
        ? ['resumo', 'anoAnterior', resumoFiltrosKey]
        : ['resumo', resumoFiltrosKey];
      
      const cachedResumo = queryClient.getQueryData(resumoQueryKey);
      
      let resumoResult;
      if (cachedResumo) {
        resumoResult = { data: cachedResumo };
      } else {
        resumoResult = await api.get(`/api/sellout/resumo?${params.toString()}`);
        queryClient.setQueryData(resumoQueryKey, resumoResult.data);
      }
      
      if (resumoResult && resumoResult.data) {
        if (anoAnterior) {
          setResumoAnoAnterior(resumoResult.data);
        } else {
          setResumo(resumoResult.data);
          setBuscandoResumo(false);
          buscandoResumoRef.current = false;
        }
      }

      if (!anoAnterior && buscarGrafico) {
        const vendasParams = new URLSearchParams();
        if (filtrosAtuais.cidade) vendasParams.append('cidade', filtrosAtuais.cidade);
        if (filtrosAtuais.uf) vendasParams.append('uf', filtrosAtuais.uf);
        if (filtrosAtuais.vendedor) vendasParams.append('vendedor', filtrosAtuais.vendedor);
        if (filtrosAtuais.dental) vendasParams.append('revenda', filtrosAtuais.dental);
        if (filtrosAtuais.produto) vendasParams.append('produto', filtrosAtuais.produto);

        const graficoFiltrosKey = JSON.stringify(filtrosAtuais);
        const graficoQueryKey = ['vendasMensais', graficoFiltrosKey];
        const cachedGrafico = queryClient.getQueryData(graficoQueryKey);
        
        if (cachedGrafico) {
          setVendasMensais(cachedGrafico);
          setBuscandoVendasMensais(false);
        } else {
          api.get(`/api/sellout/vendas-mensais?${vendasParams.toString()}`).then(vendasResult => {
            if (vendasResult && vendasResult.data && vendasResult.data.vendasMensais) {
              const dadosFormatados = vendasResult.data.vendasMensais
                .reverse()
                .map((item) => ({
                  mes: `${MESES[item.mes - 1]}`,
                  quantidade: item.quantidadeProdutosVendidos,
                  valor: item.valorProdutosVendidos,
                }));
              setVendasMensais(dadosFormatados);
              queryClient.setQueryData(graficoQueryKey, dadosFormatados);
            }
            setBuscandoVendasMensais(false);
          }).catch(error => {
            setBuscandoVendasMensais(false);
          });
        }
      } else if (!buscarGrafico) {
        setBuscandoVendasMensais(false);
      }
    } catch (error) {
      if (!anoAnterior) {
        setBuscandoResumo(false);
        buscandoResumoRef.current = false;
        setBuscandoVendasMensais(false);
      }
    }
  };

  const buscarRankingComModo = async (dentalOnly, ranking, cursor = null, direction = 'next', filtrosParaUsar = null, ordenarPorParaUsar = null, ordemParaUsar = null, limitParaUsar = null) => {
    if (buscandoRankingRef.current) {
      return;
    }
    
    buscandoRankingRef.current = true;
    setBuscandoRanking(true);
    
    try {
      const filtrosAtuais = filtrosParaUsar || filtros;
      const params = new URLSearchParams();
      if (filtrosAtuais.cidade) params.append('cidade', filtrosAtuais.cidade);
      if (filtrosAtuais.uf) params.append('uf', filtrosAtuais.uf);
      if (filtrosAtuais.vendedor) params.append('vendedor', filtrosAtuais.vendedor);
      if (filtrosAtuais.dental) params.append('revenda', filtrosAtuais.dental);
      if (filtrosAtuais.produto) params.append('produto', filtrosAtuais.produto);
      
      if (filtrosAtuais.dataInicio && filtrosAtuais.dataFim) {
        params.append('dataInicio', filtrosAtuais.dataInicio);
        params.append('dataFim', filtrosAtuais.dataFim);
      }

      const currentLimit = limitParaUsar || pagination.limit;
      const ordenarPorAtual = ordenarPorParaUsar || ordenarPor || 'pontos';
      const ordemAtual = ordemParaUsar || ordem || 'desc';

      const rankingParams = new URLSearchParams(params);
      rankingParams.append('isDentalOnly', dentalOnly.toString());
      rankingParams.append('isRanking', ranking.toString());
      rankingParams.append('ordenarPor', ordenarPorAtual);
      rankingParams.append('ordem', ordemAtual);
      rankingParams.append('limit', currentLimit);
      
      if (!cursor) {
        rankingParams.append('includeCount', 'true');
      }
      
      if (cursor) {
        rankingParams.append('cursor', cursor);
        rankingParams.append('direction', direction);
      }

      const filtrosKey = JSON.stringify(filtrosAtuais);
      const queryKey = ['ranking', dentalOnly, ranking, filtrosKey, ordenarPorAtual, ordemAtual, cursor, direction, currentLimit];
      const cachedData = queryClient.getQueryData(queryKey);
      
      let result;
      if (cachedData) {
        result = { data: cachedData };
      } else {
        result = await api.get(`/api/sellout/ranking-vendedores?${rankingParams.toString()}`);
        queryClient.setQueryData(queryKey, result.data);
      }
      
      if (result && result.data) {
        if (dentalOnly) {
          setRankingDentais(result.data.ranking || []);
        } else {
          setRankingVendedores(result.data.ranking || []);
        }
        
        const backendPagination = result.data.pagination;
        if (backendPagination) {
          const rankingData = result.data.ranking || [];
          const startPos = rankingData.length > 0 ? rankingData[0].posicao : 1;
          const endPos = rankingData.length > 0 ? rankingData[rankingData.length - 1].posicao : 0;
          
          setPagination(prev => ({
            nextCursor: backendPagination.nextCursor || null,
            prevCursor: backendPagination.prevCursor || null,
            hasNext: backendPagination.hasNext || false,
            hasPrev: backendPagination.hasPrev || false,
            limit: backendPagination.limit || currentLimit,
            startPosition: startPos,
            endPosition: endPos,
            totalCount: backendPagination.totalCount !== null && backendPagination.totalCount !== undefined 
              ? backendPagination.totalCount 
              : (prev?.totalCount || 0),
          }));

          if (backendPagination.hasNext && backendPagination.nextCursor) {
            const nextParams = new URLSearchParams(params);
            nextParams.append('isDentalOnly', dentalOnly.toString());
            nextParams.append('isRanking', ranking.toString());
            nextParams.append('ordenarPor', ordenarPorAtual);
            nextParams.append('ordem', ordemAtual);
            nextParams.append('limit', currentLimit);
            nextParams.append('cursor', backendPagination.nextCursor);
            nextParams.append('direction', 'next');
            
            const nextQueryKey = ['ranking', dentalOnly, ranking, filtrosKey, ordenarPorAtual, ordemAtual, backendPagination.nextCursor, 'next', currentLimit];
            const nextCachedData = queryClient.getQueryData(nextQueryKey);
            
            if (!nextCachedData) {
              api.get(`/api/sellout/ranking-vendedores?${nextParams.toString()}`).then(nextResult => {
                if (nextResult && nextResult.data) {
                  queryClient.setQueryData(nextQueryKey, nextResult.data);
                }
              }).catch(() => {});
            }
          }

          if (backendPagination.hasPrev && backendPagination.prevCursor) {
            const prevParams = new URLSearchParams(params);
            prevParams.append('isDentalOnly', dentalOnly.toString());
            prevParams.append('isRanking', ranking.toString());
            prevParams.append('ordenarPor', ordenarPorAtual);
            prevParams.append('ordem', ordemAtual);
            prevParams.append('limit', currentLimit);
            prevParams.append('cursor', backendPagination.prevCursor);
            prevParams.append('direction', 'prev');
            
            const prevQueryKey = ['ranking', dentalOnly, ranking, filtrosKey, ordenarPorAtual, ordemAtual, backendPagination.prevCursor, 'prev', currentLimit];
            const prevCachedData = queryClient.getQueryData(prevQueryKey);
            
            if (!prevCachedData) {
              api.get(`/api/sellout/ranking-vendedores?${prevParams.toString()}`).then(prevResult => {
                if (prevResult && prevResult.data) {
                  queryClient.setQueryData(prevQueryKey, prevResult.data);
                }
              }).catch(() => {});
            }
          }
        }
      }
    } catch (error) {
    } finally {
      setBuscandoRanking(false);
      buscandoRankingRef.current = false;
    }
  };

  const buscarRanking = async (cursor = null, direction = 'next', limitParaUsar = null) => {
    return buscarRankingComModo(isDentalOnly, isRanking, cursor, direction, null, null, null, limitParaUsar);
  };

  const handleFiltroChange = (campo, valor) => {
    const novosFiltros = { ...filtros, [campo]: valor };
    
    if (campo === 'uf') {
      novosFiltros.cidade = '';
      novosFiltros.dental = '';
      novosFiltros.produto = '';
      novosFiltros.vendedor = '';
    }
    if (campo === 'cidade') {
      novosFiltros.dental = '';
      novosFiltros.produto = '';
      novosFiltros.vendedor = '';
    }
    if (campo === 'dental') {
      novosFiltros.produto = '';
      novosFiltros.vendedor = '';
    }
    
    setFiltros(novosFiltros);
    setPagination((prev) => ({
      ...prev,
      nextCursor: null,
      prevCursor: null,
      hasNext: false,
      hasPrev: false,
    }));
  };


  const handleFlipCard = async (cardType) => {
    const vaiVirar = !cardFlipped[cardType];
    
    const novoEstado = { ...cardFlipped, [cardType]: vaiVirar };
    setCardFlipped(novoEstado);
    
    if (vaiVirar) {
      const precisaBuscar = 
        (cardType === 'valor' && !resumoAnoAnterior.valorProdutosVendidos) ||
        (cardType === 'pontos' && !resumoAnoAnterior.quantidadePontosEntregues) ||
        (cardType === 'quantidade' && !resumoAnoAnterior.quantidadeProdutosVendidos);
      
      if (precisaBuscar) {
        setBuscandoAnoAnterior(prev => ({ ...prev, [cardType]: true }));
        buscarResumoApenas(true).finally(() => {
          setBuscandoAnoAnterior(prev => ({ ...prev, [cardType]: false }));
        });
      }
    }
  };

  const handleOrdenacao = (coluna) => {
    if (ordenarPor === coluna) {
      setOrdem(ordem === 'asc' ? 'desc' : 'asc');
    } else {
      setOrdenarPor(coluna);
      setOrdem('asc');
    }
    setPagination((prev) => ({
      ...prev,
      nextCursor: null,
      prevCursor: null,
      hasNext: false,
      hasPrev: false,
    }));
  };

  const formatarMoeda = (valor) => {
    return Number(valor)
      .toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
      .toString()
      .replace(/\u00a0/g, ' ');
  };

  const formatarNumero = (valor) => {
    return Number(valor).toLocaleString('pt-BR');
  };

  const formatarQuantidade = (valor) => {
    return Number(valor).toLocaleString('pt-BR');
  };

  const rankingAtual = isDentalOnly ? rankingDentais : rankingVendedores;

  return (
    <Container fluid className="sellout-container">
      <div style={{ padding: '20px' }}>
        <h4 className="sellout-title">Sell Out</h4>
        <hr className="sellout-hr" />

      <div className="sellout-filters-row">
        <div className="sellout-filter-col">
          <label className="sellout-filter-label">UF</label>
          <select
            className="sellout-select"
            value={filtros.uf}
            onChange={(e) => handleFiltroChange('uf', e.target.value)}
          >
            <option value="">Todas</option>
            {ESTADOS_BRASIL.map((estado) => (
              <option key={estado.value} value={estado.value}>
                {estado.value}
              </option>
            ))}
          </select>
        </div>

        <div className="sellout-filter-col">
          <label className="sellout-filter-label">Cidade</label>
          <select
            className="sellout-select"
            value={filtros.cidade}
            onChange={(e) => handleFiltroChange('cidade', e.target.value)}
          >
            <option value="">Todas</option>
            {cidades.map((cidade) => (
              <option key={cidade} value={cidade}>
                {cidade}
              </option>
            ))}
          </select>
        </div>

        <div className="sellout-filter-col">
          <label className="sellout-filter-label">Dental</label>
          <select
            className="sellout-select"
            value={filtros.dental}
            onChange={(e) => handleFiltroChange('dental', e.target.value)}
          >
            <option value="">Todos</option>
            {dentais.map((dental) => (
              <option key={dental.id} value={dental.id}>
                {dental.nomeFantasia}
              </option>
            ))}
          </select>
        </div>

        <div className="sellout-filter-col">
          <label className="sellout-filter-label">Vendedor</label>
          <select
            className="sellout-select"
            value={filtros.vendedor}
            onChange={(e) => handleFiltroChange('vendedor', e.target.value)}
            disabled={!filtros.dental}
          >
            <option value="">Todos</option>
            {vendedores.map((vendedor) => (
              <option key={vendedor.id} value={vendedor.id}>
                {vendedor.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="sellout-filter-col">
          <label className="sellout-filter-label">Produto</label>
          <Select
            className="sellout-react-select"
            classNamePrefix="sellout-select"
            value={produtos.find(p => p.value === filtros.produto) || null}
            onChange={(selected) => handleFiltroChange('produto', selected ? selected.value : '')}
            options={produtos}
            placeholder="Todos"
            isClearable
            isSearchable
            noOptionsMessage={() => 'Nenhum produto encontrado'}
          />
        </div>

        <div className="sellout-filter-col">
          <label className="sellout-filter-label">Data Início</label>
          <input
            type="date"
            className="sellout-select"
            value={filtros.dataInicio}
            onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
          />
        </div>

        <div className="sellout-filter-col">
          <label className="sellout-filter-label">Data Fim</label>
          <input
            type="date"
            className="sellout-select"
            value={filtros.dataFim}
            onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
          />
        </div>

        <div className="sellout-filter-col sellout-filter-col-buttons">
          <label className="sellout-filter-label" style={{ visibility: 'hidden' }}>Ações</label>
          <div className="sellout-buttons-container">
            <button
              type="button"
              className="sellout-btn-buscar"
              onClick={() => {
                const filtrosKey = JSON.stringify(filtros);
                queryClient.invalidateQueries(['ranking']);
                queryClient.invalidateQueries(['resumo']);
                queryClient.invalidateQueries(['vendasMensais']);
                
                setPagination(prev => ({
                  ...prev,
                  nextCursor: null,
                  prevCursor: null,
                  hasNext: false,
                  hasPrev: false,
                }));
                buscarRanking();
                
                const apenasDatasMudaram = 
                  ultimosFiltrosRef.current.cidade === filtros.cidade &&
                  ultimosFiltrosRef.current.uf === filtros.uf &&
                  ultimosFiltrosRef.current.vendedor === filtros.vendedor &&
                  ultimosFiltrosRef.current.dental === filtros.dental &&
                  ultimosFiltrosRef.current.produto === filtros.produto &&
                  (ultimosFiltrosRef.current.dataInicio !== filtros.dataInicio ||
                   ultimosFiltrosRef.current.dataFim !== filtros.dataFim);
                
                buscarDados(false, !apenasDatasMudaram);
                ultimosFiltrosRef.current = { ...filtros };
              }}
              title="Buscar"
            >
              <i className="fas fa-search"></i>
            </button>
            <button
              type="button"
              className="sellout-btn-limpar"
              onClick={() => {
                const filtrosPadrao = {
                  cidade: '',
                  uf: '',
                  vendedor: '',
                  dental: '',
                  produto: '',
                  dataInicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
                  dataFim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
                };
                setFiltros(filtrosPadrao);
                setIsDentalOnly(false);
                setIsRanking(false);
                setOrdenarPor('pontos');
                setOrdem('desc');
                setPagination(prev => ({
                  ...prev,
                  nextCursor: null,
                  prevCursor: null,
                  hasNext: false,
                  hasPrev: false,
                }));
                ultimosFiltrosRef.current = { ...filtrosPadrao };
                buscarRankingComModo(false, false, null, 'next', filtrosPadrao, 'pontos', 'desc');
                buscarDados(false, true, filtrosPadrao);
              }}
              title="Limpar filtros"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>

      <Row className="sellout-cards-row">
        <Col xs={12} md={4} className="sellout-card-col">
          <div 
            className={`sellout-card ${cardFlipped.valor ? 'flipped' : ''}`}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <div className="sellout-card-inner">
              <div className="sellout-card-front">
                <button 
                  className="sellout-card-flip-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFlipCard('valor');
                  }}
                >
                  <i className="fas fa-exchange-alt"></i>
                </button>
                <div className="sellout-card-title">
                  R$ Produtos vendidos
                </div>
                <div className="sellout-card-value">
                  {buscandoResumo ? (
                    <i className="fas fa-circle-notch fa-spin"></i>
                  ) : (
                    formatarMoeda(resumo.valorProdutosVendidos)
                  )}
                </div>
              </div>
              <div className="sellout-card-back">
                <button 
                  className="sellout-card-flip-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFlipCard('valor');
                  }}
                >
                  <i className="fas fa-exchange-alt"></i>
                </button>
                <div className="sellout-card-title">
                  <span className="sellout-card-ano-anterior">ANO ANTERIOR - </span>
                  R$ Produtos vendidos
                </div>
                <div className="sellout-card-value">
                  {buscandoAnoAnterior.valor ? (
                    <i className="fas fa-circle-notch fa-spin"></i>
                  ) : (
                    formatarMoeda(resumoAnoAnterior.valorProdutosVendidos)
                  )}
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col xs={12} md={4} className="sellout-card-col">
          <div 
            className={`sellout-card ${cardFlipped.pontos ? 'flipped' : ''}`}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <div className="sellout-card-inner">
              <div className="sellout-card-front">
                <button 
                  className="sellout-card-flip-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFlipCard('pontos');
                  }}
                >
                  <i className="fas fa-exchange-alt"></i>
                </button>
                <div className="sellout-card-title">
                  Pontos entregues
                </div>
                <div className="sellout-card-value">
                  {buscandoResumo ? (
                    <i className="fas fa-circle-notch fa-spin"></i>
                  ) : (
                    formatarNumero(resumo.quantidadePontosEntregues)
                  )}
                </div>
              </div>
              <div className="sellout-card-back">
                <button 
                  className="sellout-card-flip-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFlipCard('pontos');
                  }}
                >
                  <i className="fas fa-exchange-alt"></i>
                </button>
                <div className="sellout-card-title">
                  <span className="sellout-card-ano-anterior">ANO ANTERIOR - </span>
                  Pontos entregues
                </div>
                <div className="sellout-card-value">
                  {buscandoAnoAnterior.pontos ? (
                    <i className="fas fa-circle-notch fa-spin"></i>
                  ) : (
                    formatarNumero(resumoAnoAnterior.quantidadePontosEntregues)
                  )}
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col xs={12} md={4} className="sellout-card-col">
          <div 
            className={`sellout-card ${cardFlipped.quantidade ? 'flipped' : ''}`}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <div className="sellout-card-inner">
              <div className="sellout-card-front">
                <button 
                  className="sellout-card-flip-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFlipCard('quantidade');
                  }}
                >
                  <i className="fas fa-exchange-alt"></i>
                </button>
                <div className="sellout-card-title">
                  Produtos vendidos
                </div>
                <div className="sellout-card-value">
                  {buscandoResumo ? (
                    <i className="fas fa-circle-notch fa-spin"></i>
                  ) : (
                    formatarNumero(resumo.quantidadeProdutosVendidos)
                  )}
                </div>
              </div>
              <div className="sellout-card-back">
                <button 
                  className="sellout-card-flip-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFlipCard('quantidade');
                  }}
                >
                  <i className="fas fa-exchange-alt"></i>
                </button>
                <div className="sellout-card-title">
                  <span className="sellout-card-ano-anterior">ANO ANTERIOR - </span>
                  Produtos vendidos
                </div>
                <div className="sellout-card-value">
                  {buscandoAnoAnterior.quantidade ? (
                    <i className="fas fa-circle-notch fa-spin"></i>
                  ) : (
                    formatarNumero(resumoAnoAnterior.quantidadeProdutosVendidos)
                  )}
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="sellout-charts-row">
        <Col xs={12} md={6} className="sellout-chart-col">
          <div className="sellout-chart-container">
            <div className="sellout-chart-title">
              Produtos vendidos
            </div>
            <div className="sellout-chart-subtitle">Mês a Mês</div>
            <div className="sellout-chart-content">
              {buscandoVendasMensais || vendasMensais.length === 0 ? (
                <div className="sellout-chart-loading">
                  <i className="fas fa-circle-notch fa-spin"></i>
                  <span>Carregando dados...</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={vendasMensais}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="mes" 
                      tick={{ fill: '#666', fontSize: 12 }}
                      stroke="#999"
                    />
                    <YAxis 
                      tick={{ fill: '#666', fontSize: 12 }}
                      stroke="#999"
                      tickFormatter={(value) => formatarNumero(value)}
                    />
                    <Tooltip 
                      formatter={(value) => formatarNumero(value)}
                      labelFormatter={(label) => `Mês: ${label}`}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="quantidade"
                      stroke="#014677"
                      strokeWidth={2}
                      dot={{ fill: '#014677', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Quantidade"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </Col>

        <Col xs={12} md={6} className="sellout-chart-col">
          <div className="sellout-chart-container">
            <div className="sellout-chart-title">
              R$ Produtos vendidos
            </div>
            <div className="sellout-chart-subtitle">Mês a Mês</div>
            <div className="sellout-chart-content">
              {buscandoVendasMensais || vendasMensais.length === 0 ? (
                <div className="sellout-chart-loading">
                  <i className="fas fa-circle-notch fa-spin"></i>
                  <span>Carregando dados...</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={vendasMensais}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="mes" 
                      tick={{ fill: '#666', fontSize: 12 }}
                      stroke="#999"
                    />
                    <YAxis 
                      tick={{ fill: '#666', fontSize: 12 }}
                      stroke="#999"
                      width={130}
                      tickFormatter={(value) => {
                        return formatarMoeda(value);
                      }}
                    />
                    <Tooltip 
                      formatter={(value) => formatarMoeda(value)}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="valor"
                      stroke="#014677"
                      strokeWidth={2}
                      dot={{ fill: '#014677', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row className="sellout-ranking-row">
        <Col xs={12}>
          <div className="sellout-ranking-header">
            <div className="sellout-ranking-toggle">
              <label className="sellout-toggle-label">Visualizar por:</label>
              <div className="sellout-toggle-buttons">
                <button
                  type="button"
                  className={`sellout-toggle-btn ${!isDentalOnly ? 'active' : ''}`}
                  onClick={() => {
                    setPagination((prev) => ({
                      ...prev,
                      nextCursor: null,
                      prevCursor: null,
                      hasNext: false,
                      hasPrev: false,
                    }));
                    setIsDentalOnly(false);
                  }}
                >
                  Vendedor
                </button>
                <button
                  type="button"
                  className={`sellout-toggle-btn ${isDentalOnly ? 'active' : ''}`}
                  onClick={() => {
                    setPagination((prev) => ({
                      ...prev,
                      nextCursor: null,
                      prevCursor: null,
                      hasNext: false,
                      hasPrev: false,
                    }));
                    setIsDentalOnly(true);
                  }}
                >
                  Dental
                </button>
              </div>
            </div>
            <div className="sellout-ranking-toggle">
              <label className="sellout-toggle-label">Modo:</label>
              <div className="sellout-toggle-buttons">
                <button
                  type="button"
                  className={`sellout-toggle-btn ${!isRanking ? 'active' : ''}`}
                  onClick={() => {
                    setPagination((prev) => ({
                      ...prev,
                      nextCursor: null,
                      prevCursor: null,
                      hasNext: false,
                      hasPrev: false,
                    }));
                    setIsRanking(false);
                  }}
                >
                  Analítico
                </button>
                <button
                  type="button"
                  className={`sellout-toggle-btn ${isRanking ? 'active' : ''}`}
                  onClick={() => {
                    setPagination((prev) => ({
                      ...prev,
                      nextCursor: null,
                      prevCursor: null,
                      hasNext: false,
                      hasPrev: false,
                    }));
                    setIsRanking(true);
                  }}
                >
                  Ranking
                </button>
              </div>
            </div>
          </div>

          <div className="sellout-table-container">
            <table className="sellout-table">
              <thead>
                <tr>
                  <th className="sellout-th-position"></th>
                  {!isDentalOnly && (
                    <th 
                      className="sellout-th-sortable"
                      onClick={() => handleOrdenacao('nome')}
                    >
                      Vendedor
                      {ordenarPor === 'nome' ? (
                        <i className={`fas fa-sort-${ordem === 'asc' ? 'up' : 'down'} sellout-sort-icon`}></i>
                      ) : (
                        <i className="fas fa-sort sellout-sort-icon-inactive"></i>
                      )}
                    </th>
                  )}
                  <th 
                    className="sellout-th-sortable"
                    onClick={() => handleOrdenacao('revenda')}
                  >
                    {isDentalOnly ? 'Dental' : 'Dental do vendedor'}
                    {ordenarPor === 'revenda' ? (
                      <i className={`fas fa-sort-${ordem === 'asc' ? 'up' : 'down'} sellout-sort-icon`}></i>
                    ) : (
                      <i className="fas fa-sort sellout-sort-icon-inactive"></i>
                    )}
                  </th>
                  {!isRanking && (
                    <th 
                      className="sellout-th-sortable"
                      onClick={() => handleOrdenacao('nomeproduto')}
                    >
                      Produto
                      {ordenarPor === 'nomeproduto' ? (
                        <i className={`fas fa-sort-${ordem === 'asc' ? 'up' : 'down'} sellout-sort-icon`}></i>
                      ) : (
                        <i className="fas fa-sort sellout-sort-icon-inactive"></i>
                      )}
                    </th>
                  )}
                  <th 
                    className="sellout-th-sortable"
                    onClick={() => handleOrdenacao('quantidade')}
                  >
                    Quantidade
                    {ordenarPor === 'quantidade' ? (
                      <i className={`fas fa-sort-${ordem === 'asc' ? 'up' : 'down'} sellout-sort-icon`}></i>
                    ) : (
                      <i className="fas fa-sort sellout-sort-icon-inactive"></i>
                    )}
                  </th>
                  <th 
                    className="sellout-th-sortable"
                    onClick={() => handleOrdenacao('valorUnitario')}
                  >
                    {isRanking ? 'Média Unitária' : 'Valor unitário'}
                    {ordenarPor === 'valorUnitario' ? (
                      <i className={`fas fa-sort-${ordem === 'asc' ? 'up' : 'down'} sellout-sort-icon`}></i>
                    ) : (
                      <i className="fas fa-sort sellout-sort-icon-inactive"></i>
                    )}
                  </th>
                  <th 
                    className="sellout-th-sortable"
                    onClick={() => handleOrdenacao('valorTotal')}
                  >
                    Valor Total
                    {ordenarPor === 'valorTotal' ? (
                      <i className={`fas fa-sort-${ordem === 'asc' ? 'up' : 'down'} sellout-sort-icon`}></i>
                    ) : (
                      <i className="fas fa-sort sellout-sort-icon-inactive"></i>
                    )}
                  </th>
                  <th 
                    className="sellout-th-sortable"
                    onClick={() => handleOrdenacao('pontos')}
                  >
                    Pontos
                    {ordenarPor === 'pontos' ? (
                      <i className={`fas fa-sort-${ordem === 'asc' ? 'up' : 'down'} sellout-sort-icon`}></i>
                    ) : (
                      <i className="fas fa-sort sellout-sort-icon-inactive"></i>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {buscandoRanking ? (
                  Array.from({ length: pagination.limit }).map((_, index) => (
                    <tr key={`skeleton-${index}`} className="sellout-tr">
                      <td className="sellout-td-position">
                        <div className="sellout-skeleton"></div>
                      </td>
                      {!isDentalOnly && (
                        <td className="sellout-td">
                          <div className="sellout-skeleton"></div>
                        </td>
                      )}
                      <td className="sellout-td">
                        <div className="sellout-skeleton"></div>
                      </td>
                      <td className="sellout-td">
                        <div className="sellout-skeleton"></div>
                      </td>
                      <td className="sellout-td">
                        <div className="sellout-skeleton"></div>
                      </td>
                      <td className="sellout-td">
                        <div className="sellout-skeleton"></div>
                      </td>
                      <td className="sellout-td">
                        <div className="sellout-skeleton"></div>
                      </td>
                      <td className="sellout-td">
                        <div className="sellout-skeleton"></div>
                      </td>
                    </tr>
                  ))
                ) : rankingAtual.length === 0 ? (
                  <tr>
                    <td colSpan={isDentalOnly ? (isRanking ? 6 : 7) : (isRanking ? 7 : 8)} className="sellout-td-empty">
                      Nenhum dado encontrado
                    </td>
                  </tr>
                ) : (
                  rankingAtual.map((item) => (
                    <tr key={`${item.nome || item.dental || ''}-${item.posicao}`} className="sellout-tr">
                      <td className="sellout-td-position">{item.posicao || '-'}</td>
                      {!isDentalOnly && <td className="sellout-td">{item.nome || '-'}</td>}
                      <td className="sellout-td">{item.dental || '-'}</td>
                      {!isRanking && <td className="sellout-td">{item.produto || '-'}</td>}
                      <td className="sellout-td">{item.quantidade ? formatarQuantidade(item.quantidade) : '-'}</td>
                      <td className="sellout-td">{item.valorUnitario ? formatarMoeda(item.valorUnitario) : '-'}</td>
                      <td className="sellout-td">{item.valorTotal ? formatarMoeda(item.valorTotal) : '-'}</td>
                      <td className="sellout-td">{formatarNumero(item.pontos)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className="sellout-pagination">
              <div className="sellout-pagination-left">
                <div className="sellout-table-limit">
                  <label className="sellout-limit-label">Itens por página:</label>
                  <select
                    className="sellout-select-limit"
                    value={pagination.limit}
                    onChange={(e) => {
                      const novoLimit = parseInt(e.target.value);
                      setPagination((prev) => ({
                        ...prev,
                        limit: novoLimit,
                        nextCursor: null,
                        prevCursor: null,
                        hasNext: false,
                        hasPrev: false,
                      }));
                      buscarRanking(null, 'next', novoLimit);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                  </select>
                </div>
                {rankingAtual.length > 0 && (
                  <div className="sellout-pagination-info">
                    Mostrando {pagination.startPosition} a {pagination.endPosition} 
                    {pagination.totalCount > 0 && ` de ${formatarNumero(pagination.totalCount)}`}
                  </div>
                )}
              </div>
              {(pagination.hasNext || pagination.hasPrev) && (
                <div className="sellout-pagination-buttons">
                  <button
                    className="sellout-pagination-btn"
                    disabled={!pagination.hasPrev}
                    onClick={() => {
                      if (pagination.prevCursor) {
                        buscarRanking(pagination.prevCursor, 'prev');
                      }
                    }}
                  >
                    <i className="fas fa-chevron-left"></i>
                    Anterior
                  </button>
                  <button
                    className="sellout-pagination-btn"
                    disabled={!pagination.hasNext}
                    onClick={() => {
                      if (pagination.nextCursor) {
                        buscarRanking(pagination.nextCursor, 'next');
                      }
                    }}
                  >
                    Próxima
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
      </div>
    </Container>
  );
}
